import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AppBar, Toolbar as MuiToolbar, IconButton, Menu, MenuItem, Popover, Box } from '@mui/material';
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentIncrease,
  FormatIndentDecrease,
  FormatClear,
  Print,
  Save,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatColorText,
  FormatColorFill,
  TableChart,
} from '@mui/icons-material';

const HEADINGS = [
  { value: 'p', label: 'Párrafo' },
  { value: 'h1', label: 'Título 1' },
  { value: 'h2', label: 'Título 2' },
  { value: 'h3', label: 'Título 3' },
  { value: 'h4', label: 'Título 4' },
];

const FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
];

const SIZES = [
  { value: '1', label: '8' },
  { value: '2', label: '10' },
  { value: '3', label: '12' },
  { value: '4', label: '14' },
  { value: '5', label: '18' },
  { value: '6', label: '24' },
  { value: '7', label: '36' },
];

const COLOR_PALETTE = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
  '#ff0000', '#ff4500', '#ff9900', '#ffff00', '#00b050', '#00bcd4', '#1565c0', '#7c4dff',
  '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e4f7', '#cfe2f3', '#ead1dc', '#e6d0de',
];

const HIGHLIGHT_PALETTE = [
  '#ffff00', '#00ff00', '#00ffff', '#ff00ff',
  '#ff0000', '#0000ff', '#ffa500', '#ffffff',
  '#ffeb3b', '#c8e6c9', '#b2ebf2', '#e1bee7',
  '#ffccbc', '#f0f4c3', '#b3e5fc', '#d7ccc8',
];

const TABLE_MAX = 8;

const Separator = () => <div className="toolbar__separator" aria-hidden="true" />;

// ── TablePicker ──────────────────────────────────────────────────────────────

const TablePicker = ({ onSelect, className = '' }) => {
  const [hover, setHover] = useState({ r: 0, c: 0 });

  return (
    <div className={`toolbar__dropdown${className ? ` ${className}` : ''}`}>
      <div
        className="toolbar__table-grid"
        onMouseLeave={() => setHover({ r: 0, c: 0 })}
      >
        {Array.from({ length: TABLE_MAX }, (_, r) => (
          <div key={r} className="toolbar__table-row">
            {Array.from({ length: TABLE_MAX }, (_, c) => (
              <div
                key={c}
                className={`toolbar__table-cell${r < hover.r && c < hover.c ? ' toolbar__table-cell--active' : ''}`}
                onMouseEnter={() => setHover({ r: r + 1, c: c + 1 })}
                onClick={() => onSelect(hover.r, hover.c)}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="toolbar__dropdown-label">
        {hover.r > 0 ? `${hover.r} × ${hover.c}` : 'Seleccioná tamaño'}
      </div>
    </div>
  );
};

// ── ColorPicker ──────────────────────────────────────────────────────────────

const ColorPicker = ({ palette, onSelect, onReset, resetLabel, customInputLabel, className = '' }) => (
  <div className={`toolbar__dropdown${className ? ` ${className}` : ''}`}>
    <button type="button" className="toolbar__color-reset" onClick={onReset}>
      <span className="toolbar__color-reset-icon">⊘</span> {resetLabel}
    </button>
    <div className="toolbar__color-grid">
      {palette.map((color) => (
        <div
          key={color}
          className="toolbar__color-swatch"
          style={{ backgroundColor: color }}
          title={color}
          onClick={() => onSelect(color)}
        />
      ))}
    </div>
    <label className="toolbar__color-custom">
      <input
        type="color"
        className="toolbar__color-input"
        onChange={(e) => onSelect(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      {customInputLabel}
    </label>
  </div>
);

// ── Toolbar ──────────────────────────────────────────────────────────────────

const Toolbar = ({
  onFormat,
  onSave,
  onPrint,
  activeCommands = new Set(),
  currentBlock = 'p',
  currentFont = '',
  currentSize = '',
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [lastFontColor, setLastFontColor] = useState('#000000');
  const [lastHighlight, setLastHighlight] = useState('#ffff00');
  const [anchorAlign, setAnchorAlign] = useState(null);
  const toolbarRef = useRef(null);

  const toggleDropdown = useCallback((type, event) => {
    if (openDropdown === type) {
      setOpenDropdown(null);
      setDropdownAnchor(null);
      return;
    }
    setOpenDropdown(type);
    setDropdownAnchor(event.currentTarget);
  }, [openDropdown]);

  const handleFontColor = useCallback((color) => {
    setLastFontColor(color);
    onFormat('foreColor', color);
    setOpenDropdown(null);
    setDropdownAnchor(null);
  }, [onFormat]);

  const handleFontColorReset = useCallback(() => {
    onFormat('resetColor');
    setOpenDropdown(null);
    setDropdownAnchor(null);
  }, [onFormat]);

  const handleHighlight = useCallback((color) => {
    setLastHighlight(color);
    onFormat('hiliteColor', color);
    setOpenDropdown(null);
    setDropdownAnchor(null);
  }, [onFormat]);

  const handleHighlightReset = useCallback(() => {
    onFormat('resetHighlight');
    setOpenDropdown(null);
    setDropdownAnchor(null);
  }, [onFormat]);

  const handleTable = useCallback((rows, cols) => {
    if (rows > 0 && cols > 0) onFormat('insertTable', `${rows}x${cols}`);
    setOpenDropdown(null);
    setDropdownAnchor(null);
  }, [onFormat]);

  const handleAlignMenuOpen = (event) => {
    setAnchorAlign(event.currentTarget);
  };

  const handleAlignMenuClose = () => {
    setAnchorAlign(null);
  };

  const alignOptions = [
    { label: 'Izquierda', command: 'justifyLeft', icon: <FormatAlignLeft fontSize="small" /> },
    { label: 'Centrado', command: 'justifyCenter', icon: <FormatAlignCenter fontSize="small" /> },
    { label: 'Derecha', command: 'justifyRight', icon: <FormatAlignRight fontSize="small" /> },
    { label: 'Justificado', command: 'justifyFull', icon: <FormatAlignJustify fontSize="small" /> },
  ];

  const isBold = activeCommands.has('bold');
  const isItalic = activeCommands.has('italic');
  const isUnderline = activeCommands.has('underline');

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <MuiToolbar
        ref={toolbarRef}
        className="toolbar"
        role="toolbar"
        aria-label="Text formatting toolbar"
        variant="dense"
        onMouseDown={(e) => {
          // Prevent toolbar from stealing focus from the editor,
          // but allow <select> elements to open their native dropdown.
          if (e.target.closest('select') === null) {
            e.preventDefault();
          }
        }}
      >

        {/* Heading */}
        <select
          className="toolbar__select"
          aria-label="Heading"
          value={currentBlock}
          onChange={(e) => onFormat('formatBlock', e.target.value)}
        >
          {HEADINGS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* Font family */}
        <select
          className="toolbar__select toolbar__select--font"
          aria-label="Font family"
          value={currentFont}
          onChange={(e) => onFormat('fontName', e.target.value)}
        >
          <option value="" disabled>Fuente</option>
          {FONTS.map(({ value, label }) => (
            <option key={value} value={value} style={{ fontFamily: value }}>{label}</option>
          ))}
        </select>

        {/* Font size */}
        <select
          className="toolbar__select toolbar__select--size"
          aria-label="Font size"
          value={currentSize}
          onChange={(e) => onFormat('fontSize', e.target.value)}
        >
          <option value="" disabled>Tam.</option>
          {SIZES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <Separator />

        {/* Bold / Italic / Underline */}
        <IconButton
          aria-label="Negrita"
          aria-pressed={isBold}
          className={`toolbar__button${isBold ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={() => onFormat('bold')}
        >
          <FormatBold fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Cursiva"
          aria-pressed={isItalic}
          className={`toolbar__button${isItalic ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={() => onFormat('italic')}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Subrayado"
          aria-pressed={isUnderline}
          className={`toolbar__button${isUnderline ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={() => onFormat('underline')}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>

        <Separator />

        {/* Font color */}
        <IconButton
          aria-label="Color de fuente"
          className={`toolbar__button${openDropdown === 'fontColor' ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={(e) => toggleDropdown('fontColor', e)}
        >
          <FormatColorText fontSize="small" />
        </IconButton>

        {/* Highlight */}
        <IconButton
          aria-label="Resaltar texto"
          className={`toolbar__button${openDropdown === 'highlight' ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={(e) => toggleDropdown('highlight', e)}
        >
          <FormatColorFill fontSize="small" />
        </IconButton>

        {/* Table */}
        <IconButton
          aria-label="Insertar tabla"
          className={`toolbar__button${openDropdown === 'table' ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={(e) => toggleDropdown('table', e)}
        >
          <TableChart fontSize="small" />
        </IconButton>

        <Popover
          anchorEl={dropdownAnchor}
          open={Boolean(dropdownAnchor) && openDropdown === 'fontColor'}
          onClose={() => { setOpenDropdown(null); setDropdownAnchor(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }}
        >
          <Box sx={{ padding: 1 }}>
            <ColorPicker
              palette={COLOR_PALETTE}
              onSelect={handleFontColor}
              onReset={handleFontColorReset}
              resetLabel="Predeterminado"
              customInputLabel="Otro color..."
              className="toolbar__dropdown--menu"
            />
          </Box>
        </Popover>

        <Popover
          anchorEl={dropdownAnchor}
          open={Boolean(dropdownAnchor) && openDropdown === 'highlight'}
          onClose={() => { setOpenDropdown(null); setDropdownAnchor(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }}
        >
          <Box sx={{ padding: 1 }}>
            <ColorPicker
              palette={HIGHLIGHT_PALETTE}
              onSelect={handleHighlight}
              onReset={handleHighlightReset}
              resetLabel="Sin resaltado"
              customInputLabel="Otro color..."
              className="toolbar__dropdown--menu"
            />
          </Box>
        </Popover>

        <Popover
          anchorEl={dropdownAnchor}
          open={Boolean(dropdownAnchor) && openDropdown === 'table'}
          onClose={() => { setOpenDropdown(null); setDropdownAnchor(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }}
        >
          <Box sx={{ padding: 1 }}>
            <TablePicker onSelect={handleTable} className="toolbar__dropdown--menu" />
          </Box>
        </Popover>

        <Separator />

        {/* Alignment menu */}
        <IconButton aria-label="Alineación" size="small" className="toolbar__button" onClick={handleAlignMenuOpen}>
          {(alignOptions.find(o => activeCommands.has(o.command))?.icon) ?? <FormatAlignLeft fontSize="small" />}
        </IconButton>
        <Menu
          anchorEl={anchorAlign}
          open={Boolean(anchorAlign)}
          onClose={handleAlignMenuClose}
        >
          {alignOptions.map((option) => (
            <MenuItem
              key={option.command}
              onClick={() => {
                onFormat(option.command);
                handleAlignMenuClose();
              }}
            >
              {option.icon}
              <span style={{ marginLeft: 8 }}>{option.label}</span>
            </MenuItem>
          ))}
        </Menu>

        {/* Lists */}
        <IconButton
          aria-label="Lista con viñetas"
          className={`toolbar__button${activeCommands.has('insertUnorderedList') ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={() => onFormat('insertUnorderedList')}
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Lista numerada"
          className={`toolbar__button${activeCommands.has('insertOrderedList') ? ' toolbar__button--active' : ''}`}
          size="small"
          onClick={() => onFormat('insertOrderedList')}
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>

        {/* Indent */}
        <IconButton aria-label="Aumentar sangría" className="toolbar__button" size="small" onClick={() => onFormat('indent')}>
          <FormatIndentIncrease fontSize="small" />
        </IconButton>
        <IconButton aria-label="Disminuir sangría" className="toolbar__button" size="small" onClick={() => onFormat('outdent')}>
          <FormatIndentDecrease fontSize="small" />
        </IconButton>

        {/* Clear formatting */}
        <IconButton aria-label="Borrar formato" className="toolbar__button" size="small" onClick={() => onFormat('removeFormat')}>
          <FormatClear fontSize="small" />
        </IconButton>

        <Separator />

        {/* Print */}
        <IconButton aria-label="Imprimir" className="toolbar__button" size="small" onClick={onPrint}>
          <Print fontSize="small" />
        </IconButton>

        {/* Save */}
        <IconButton aria-label="Guardar" className="toolbar__button" size="small" onClick={onSave}>
          <Save fontSize="small" />
        </IconButton>

      </MuiToolbar>
    </AppBar>
  );
};

Toolbar.propTypes = {
  onFormat: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  activeCommands: PropTypes.instanceOf(Set),
  currentBlock: PropTypes.string,
  currentFont: PropTypes.string,
  currentSize: PropTypes.string,
};

TablePicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
};

ColorPicker.propTypes = {
  palette: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  resetLabel: PropTypes.string.isRequired,
  customInputLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default Toolbar;
